import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Sparkles, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import NotificationsBell from "@/components/app/NotificationsBell";

export default function DashLayout({ title, subtitle, nav = [], activeKey, onNav, children, testId }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = () => { logout(); navigate("/login"); };
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("shakthi_sidebar_collapsed") === "1");
  useEffect(() => {
    localStorage.setItem("shakthi_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);
  const c = collapsed;

  return (
    <div className="min-h-screen flex flex-col md:flex-row grain">
      {/* Sidebar */}
      <aside data-testid="dash-sidebar" data-collapsed={c ? "true" : "false"} className={`${c ? "md:w-16" : "md:w-64"} md:shrink-0 md:h-screen md:sticky md:top-0 border-b md:border-b-0 md:border-r border-white/5 bg-slate-950/80 backdrop-blur-xl flex flex-col transition-[width] duration-300`}>
        <div className={`p-6 flex items-center gap-3 ${c ? "md:p-3 md:flex-col" : ""}`}>
          <img
            src="/logo-mark-round.png"
            alt="Sakthi Loan Consultancy"
            className="w-10 h-10 rounded-lg bg-white p-0.5 border border-amber-500/40 shadow-lg shadow-amber-500/10 object-contain"
            data-testid="sidebar-logo"
          />
          <div className={c ? "md:hidden" : ""}>
            <div className="font-display text-lg text-white leading-none">Sakthi</div>
            <div className="text-[10px] tracking-[0.25em] text-amber-500 uppercase mt-1">{title}</div>
          </div>
          <button
            type="button"
            onClick={() => setCollapsed(v => !v)}
            data-testid="sidebar-collapse-toggle"
            title={c ? "Expand menu" : "Collapse menu"}
            className={`hidden md:flex w-8 h-8 rounded-md items-center justify-center text-slate-400 hover:text-amber-400 hover:bg-white/5 transition-colors ${c ? "" : "ml-auto"}`}
          >
            {c ? <PanelLeftOpen className="w-4 h-4"/> : <PanelLeftClose className="w-4 h-4"/>}
          </button>
        </div>
        <nav className={`px-3 py-4 flex-1 space-y-1 md:overflow-y-auto ${c ? "md:px-2" : ""}`} data-testid={`${testId}-sidebar`}>
          {nav.map((n) => (
            <button
              key={n.key}
              data-testid={`nav-${n.key}`}
              onClick={() => onNav(n.key)}
              title={n.label}
              className={`w-full text-left px-4 py-2.5 rounded-lg text-sm flex items-center gap-3 transition-colors ${c ? "md:px-0 md:justify-center" : ""} ${
                activeKey === n.key ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
              }`}>
              {n.icon && <n.icon className="w-4 h-4 shrink-0" />}
              <span className={c ? "md:hidden" : ""}>{n.label}</span>
            </button>
          ))}
        </nav>
        <div className={`p-4 border-t border-white/5 ${c ? "md:p-2" : ""}`}>
          <div className={c ? "md:hidden" : ""}>
            <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">Signed in</div>
            <div className="text-white text-sm font-medium">{user?.name}</div>
            <div className="text-xs text-slate-500 font-mono-alt">{user?.phone}</div>
          </div>
          <Button onClick={doLogout} data-testid="logout-btn" size="sm" variant="ghost" title="Logout" className={`mt-3 w-full text-slate-400 hover:text-red-400 hover:bg-red-500/10 justify-start ${c ? "md:mt-0 md:justify-center md:px-0" : ""}`}>
            <LogOut className={`w-4 h-4 ${c ? "md:mr-0" : ""} mr-2`} /><span className={c ? "md:hidden" : ""}>Logout</span>
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="border-b border-white/5 px-8 py-6 backdrop-blur-xl bg-slate-950/40 sticky top-0 z-10 flex items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-amber-500 font-bold">{subtitle}</div>
            <h1 className="font-display text-3xl text-white mt-1">{nav.find((n) => n.key === activeKey)?.label || title}</h1>
          </div>
          <NotificationsBell/>
        </div>
        <div className="p-8 space-y-6">{children}</div>
      </main>
    </div>
  );
}
